import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { pick } from 'lodash-es';
import { NgxMaskDirective } from 'ngx-mask';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktFormComponent, formInput, formRadioGroup, formRow } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';

import { Patterns } from '@shared/components';
import { MESSAGE } from '@shared/constants';
import { AddMocketBankAccountParams, FormModel } from '@shared/models';
import { MocketBankService, NotificationService } from '@shared/services';
import { ErrorUtils, getFormattedAmount } from '@shared/utils';

@Component({
  selector: 'app-add-mocket-bank-account-modal',
  imports: [RktIconComponent, RktFormComponent, FormsModule, ReactiveFormsModule, RktFormComponent, RktButtonDirective, NgxMaskDirective],
  templateUrl: './add-mocket-bank-account-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class AddMocketBankAccountModalComponent implements AfterViewChecked {
  bankForm = new FormGroup({});

  isEdit = false;

  formError = '';

  formLoading = false;

  editMocketBankId?: string;

  accountBalance = new FormControl();

  financialAccountFields = [
    formRow([
      formInput({
        key: 'name',
        label: 'Financial Account Nam\u0435',
        props: {
          pattern: Patterns.GENERIC_NAME,
          required: true,
        },
        validation: { messages: { pattern: 'Enter a valid Financial Account Nam\u0435' } },
      }),
    ]),
  ];

  bankFields: FormlyFieldConfig[] = [
    formRow([
      formRadioGroup({
        key: 'type',
        label: 'Bank Account Type',
        props: {
          required: true,
          options: [
            { label: 'Checking', value: 'CHECKING' },
            { label: 'Saving', value: 'SAVING' },
          ],
        },
        defaultValue: 'CHECKING',
      }),
    ]),
  ];

  expressions = {
    'props.disabled': '!!model.isEdit',
    'props.required': '!model.isEdit',
  };

  accountNumberFields: FormlyFieldConfig[] = [
    formRow([
      formInput({
        key: 'accountNumber',
        label: 'Account Number',
        props: {
          minLength: 9,
          maxLength: 18,
          required: true,
        },
        expressions: this.expressions,
      }),
    ]),
  ];

  formModel: FormModel<AddMocketBankAccountParams> = {
    accountHolderType: 'BUSINESS_ACCOUNT',
  };

  private destroy$ = new Subject<void>();

  constructor(
    private ref: ChangeDetectorRef,
    public activeModal: ActiveModal,
    private mocketBankService: MocketBankService,
    private notificationService: NotificationService,
  ) {}

  modalInitData(data: Partial<AddMocketBankAccountParams>) {
    this.isEdit = true;
    this.editMocketBankId = data.id;
    this.formModel = {
      ...this.formModel,
      ...data,
      accountNumber: data.maskedAccountNumber!,
      isEdit: true,
    };

    this.accountBalance.setValue(data.balance);
  }

  ngAfterViewChecked() {
    this.ref.detectChanges();
  }

  public onSubmit(): void {
    this.bankForm.markAllAsTouched();

    if (this.bankForm.invalid) {
      return;
    }

    this.formLoading = true;
    this.formError = '';

    const { name, accountHolderType, type, accountNumber } = this.formModel;

    const accountCreateData: AddMocketBankAccountParams = {
      name,
      type,
      accountHolderType,
      accountNumber,
      balance: Number(this.accountBalance.value),
      currency: 'USD',
    };

    if (this.isEdit && this.editMocketBankId) {
      const updateData = pick(accountCreateData, ['name', 'type', 'balance', 'currency']);
      this.mocketBankService
        .updateMocketBankAccount(updateData, this.editMocketBankId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.formLoading = false;
            this.activeModal.close(response);
            this.notificationService.displaySuccess('Test Account Updated.', 'Success');
          },
          error: (error) => {
            this.handleError(error);
            ErrorUtils.catchError('mocketBankService.updateMocketBankAccount error', error);
          },
        });
    } else {
      this.mocketBankService
        .addMocketBankAccount(accountCreateData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.formLoading = false;
            this.activeModal.close(true);
            this.notificationService.displaySuccess('Test Account Created.', 'Success');
          },
          error: (error) => {
            this.handleError(error);
            ErrorUtils.catchError('mocketBankService.addMocketBankAccount error', error);
          },
        });
    }
  }

  onAmountFieldBlur(): void {
    if (!this.accountBalance.value) {
      this.accountBalance.reset();
    } else {
      const value = parseFloat(this.accountBalance.value);
      this.accountBalance.setValue(getFormattedAmount(value).formattedValue);
    }
  }

  public onAccountNumberChange(): void {
    // No-op: confirmation field removed
  }

  public handleError(error: string | number) {
    this.formLoading = false;
    this.formError = typeof error === 'string' ? error : MESSAGE.GENERIC_ERROR;
  }
}
